import { cn } from "@/lib/utils";
import { useState } from "react";

export interface ChargingWindow {
  start: number; // 0-23
  end: number;   // 0-23 (can wrap past midnight)
  pct: number;   // percentage of daily energy
}

export interface FleetTypeConfig {
  type: string;
  n: number;
  kwhPerVehicle: number; // kWh per vehicle per year
  windows: ChargingWindow[];
}

interface FleetProfileEditorProps {
  config: FleetTypeConfig[];
  onChange: (config: FleetTypeConfig[]) => void;
}

/** Get hours covered by a window (handles midnight wraparound) */
export function getWindowHours(w: ChargingWindow): number[] {
  const hours: number[] = [];
  if (w.start <= w.end) {
    for (let h = w.start; h < w.end; h++) hours.push(h);
  } else {
    // Wraparound: e.g. 23:00 - 04:00 => [23,0,1,2,3]
    for (let h = w.start; h < 24; h++) hours.push(h);
    for (let h = 0; h < w.end; h++) hours.push(h);
  }
  if (hours.length === 0) hours.push(w.start); // single hour fallback
  return hours;
}

/**
 * Build a 24-element array of relative demand weights from fleet config.
 * Each element = total kWh that should be consumed in that hour (unnormalized).
 * Returns [hourlyKwh[0..23], totalKwhPerYear]
 */
export function buildFleetDemandProfile(config: FleetTypeConfig[]): [number[], number] {
  const hourly = new Array(24).fill(0);
  let totalKwhYear = 0;

  for (const ft of config) {
    if (ft.n <= 0 || ft.kwhPerVehicle <= 0) continue;
    const yearlyKwh = ft.n * ft.kwhPerVehicle;
    totalKwhYear += yearlyKwh;
    const dailyKwh = yearlyKwh / 365.25;

    for (const w of ft.windows) {
      const hours = getWindowHours(w);
      if (hours.length === 0) continue;
      const kwhPerHour = (dailyKwh * w.pct / 100) / hours.length;
      for (const h of hours) {
        hourly[h] += kwhPerHour;
      }
    }
  }

  return [hourly, totalKwhYear];
}

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

const TYPE_COLORS = [
  "#0B1F4A", // Bus met laadbak
  "#00A3C4", // Combi Afzet
  "#2563EB", // Combi KV-D/E LZV
  "#16A34A", // Trekker
  "#D97706", // Trekker 6x2 VLA
  "#DC2626", // Trekker Bulk
  "#7C3AED", // Trekker DC
  "#EC4899", // Trekker LWD
];

/** Build per-type hourly demand: returns number[][] (types x 24h) */
function buildFleetDemandByType(config: FleetTypeConfig[]): number[][] {
  return config.map(ft => {
    const hourly = new Array(24).fill(0);
    if (ft.n <= 0 || ft.kwhPerVehicle <= 0) return hourly;
    const dailyKwh = (ft.n * ft.kwhPerVehicle) / 365.25;
    for (const w of ft.windows) {
      const hours = getWindowHours(w);
      if (hours.length === 0) continue;
      const kwhPerHour = (dailyKwh * w.pct / 100) / hours.length;
      for (const h of hours) hourly[h] += kwhPerHour;
    }
    return hourly;
  });
}

function LoadProfileChart({ config }: { config: FleetTypeConfig[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const byType = buildFleetDemandByType(config);
  // totals per hour
  const hourlyTotal = Array.from({ length: 24 }, (_, h) => byType.reduce((s, t) => s + t[h], 0));
  const max = Math.max(...hourlyTotal, 1);

  const W = 360, H = 110, PAD_L = 36, PAD_B = 16, PAD_T = 8;
  const chartW = W - PAD_L, chartH = H - PAD_B - PAD_T;
  const barW = chartW / 24 - 1;
  const xLabels = [0, 3, 6, 9, 12, 15, 18, 21];
  const activeTypes = config.map((ft, i) => ({ idx: i, type: ft.type, n: ft.n })).filter(t => t.n > 0);

  return (
    <div className="rounded-lg border border-[#D9E2EC] bg-[#F5F8FB] p-2.5">
      <span className="text-[9px] font-semibold text-[#0B1F4A] mb-1 block">24-uurs laadprofiel (gemiddeld vermogen)</span>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 130 }}>
        {/* grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(f => {
          const y = PAD_T + chartH * (1 - f);
          return <line key={f} x1={PAD_L} x2={W} y1={y} y2={y} stroke="#D9E2EC" strokeWidth={0.5} />;
        })}
        {/* y-axis labels */}
        <text x={PAD_L - 2} y={PAD_T + 3} textAnchor="end" fontSize={7} fill="#8899AA">{(max/1000).toFixed(1)} MW</text>
        <text x={PAD_L - 2} y={PAD_T + chartH + 1} textAnchor="end" fontSize={7} fill="#8899AA">0</text>
        {/* stacked bars */}
        {Array.from({ length: 24 }, (_, hour) => {
          const x = PAD_L + hour * (chartW / 24) + 0.5;
          let cumulative = 0;
          const segments = config.map((_, ti) => {
            const v = byType[ti][hour];
            if (v <= 0) return null;
            const segH = (v / max) * chartH;
            const segY = PAD_T + chartH - cumulative - segH;
            cumulative += segH;
            return <rect key={ti} x={x} y={segY} width={barW} height={segH} fill={TYPE_COLORS[ti]} opacity={hovered === hour ? 1 : 0.85} rx={0.5} style={{ transition: 'y 0.3s ease, height 0.3s ease' }} />;
          });
          return (
            <g key={hour}
              onMouseEnter={() => setHovered(hour)}
              onMouseLeave={() => setHovered(null)}
            >
              {/* invisible hit area */}
              <rect x={x} y={PAD_T} width={barW} height={chartH} fill="transparent" style={{ cursor: 'crosshair' }} />
              {segments}
            </g>
          );
        })}
        {/* hover tooltip */}
        {hovered !== null && hourlyTotal[hovered] > 0 && (() => {
          const x = PAD_L + hovered * (chartW / 24) + barW / 2;
          const breakdowns: { label: string; value: number; color: string }[] = [];
          config.forEach((ft, ti) => {
            if (byType[ti][hovered] > 0) breakdowns.push({ label: ft.type.split(' ').slice(0, 2).join(' '), value: byType[ti][hovered] / 1000, color: TYPE_COLORS[ti] });
          });
          const tooltipW = 82;
          const tooltipH = 12 + breakdowns.length * 9 + 4;
          const tooltipX = hovered > 16 ? x - tooltipW - 4 : x - tooltipW / 2;
          const clampedX = Math.max(PAD_L, Math.min(W - tooltipW - 2, tooltipX));
          return (
            <g style={{ pointerEvents: 'none' }}>
              {/* shadow */}
              <rect x={clampedX - 1} y={0} width={tooltipW + 2} height={tooltipH + 2} fill="black" opacity={0.08} rx={4} />
              {/* background */}
              <rect x={clampedX} y={0} width={tooltipW} height={tooltipH} fill="white" stroke="#D9E2EC" strokeWidth={0.4} rx={3.5} />
              {/* header */}
              <text x={clampedX + tooltipW / 2} y={10} textAnchor="middle" fontSize={6.5} fill="#0B1F4A" fontWeight="bold">
                {String(hovered).padStart(2, '0')}:00 — {(hourlyTotal[hovered]/1000).toFixed(2)} MW
              </text>
              {/* separator */}
              <line x1={clampedX + 4} x2={clampedX + tooltipW - 4} y1={13} y2={13} stroke="#D9E2EC" strokeWidth={0.4} />
              {/* breakdown rows */}
              {breakdowns.map((b, i) => (
                <g key={i}>
                  <rect x={clampedX + 4} y={16 + i * 9} width={4} height={4} rx={1} fill={b.color} />
                  <text x={clampedX + 10} y={19.5 + i * 9} fontSize={5.5} fill="#4A5568">{b.label}</text>
                  <text x={clampedX + tooltipW - 5} y={19.5 + i * 9} textAnchor="end" fontSize={5.5} fill="#0B1F4A" fontWeight="600">{b.value.toFixed(2)} MW</text>
                </g>
              ))}
            </g>
          );
        })()}
        {/* x-axis labels */}
        {xLabels.map(h => (
          <text key={h} x={PAD_L + h * (chartW / 24) + barW / 2} y={H - 2} textAnchor="middle" fontSize={7} fill="#8899AA">
            {String(h).padStart(2, '0')}
          </text>
        ))}
      </svg>
      {/* Legend */}
      {activeTypes.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {activeTypes.map(t => (
            <div key={t.idx} className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: TYPE_COLORS[t.idx] }} />
              <span className="text-[8px] text-[#0B1F4A]">{t.type}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function FleetProfileEditor({ config, onChange }: FleetProfileEditorProps) {
  const updateType = (idx: number, patch: Partial<FleetTypeConfig>) => {
    const next = config.map((c, i) => i === idx ? { ...c, ...patch } : c);
    onChange(next);
  };

  const updateWindow = (typeIdx: number, winIdx: number, patch: Partial<ChargingWindow>) => {
    const next = config.map((c, i) => {
      if (i !== typeIdx) return c;
      const windows = c.windows.map((w, j) => j === winIdx ? { ...w, ...patch } : w);
      return { ...c, windows };
    });
    onChange(next);
  };

  const addWindow = (typeIdx: number) => {
    const next = config.map((c, i) => {
      if (i !== typeIdx || c.windows.length >= 3) return c;
      return { ...c, windows: [...c.windows, { start: 8, end: 12, pct: 0 }] };
    });
    onChange(next);
  };

  const removeWindow = (typeIdx: number, winIdx: number) => {
    const next = config.map((c, i) => {
      if (i !== typeIdx || c.windows.length <= 1) return c;
      return { ...c, windows: c.windows.filter((_, j) => j !== winIdx) };
    });
    onChange(next);
  };

  const addVehicleType = () => {
    if (config.length >= 10) return;
    onChange([...config, {
      type: `Type ${config.length + 1}`,
      n: 0,
      kwhPerVehicle: 80000,
      windows: [{ start: 8, end: 16, pct: 100 }],
    }]);
  };

  const removeVehicleType = (idx: number) => {
    if (config.length <= 1) return;
    onChange(config.filter((_, i) => i !== idx));
  };

  const totalVehicles = config.reduce((s, c) => s + c.n, 0);
  const totalMwh = config.reduce((s, c) => s + c.n * c.kwhPerVehicle, 0) / 1e3;

  return (
    <div className="space-y-2">
      {config.map((ft, ti) => {
        const pctSum = ft.windows.reduce((s, w) => s + w.pct, 0);
        const valid = Math.abs(pctSum - 100) < 0.5;
        return (
          <div key={ti} className="rounded-lg border border-[#D9E2EC] bg-[#F5F8FB] p-2.5">
            {/* Type header */}
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <input
                type="text"
                value={ft.type}
                onChange={e => updateType(ti, { type: e.target.value })}
                className="text-[11px] font-semibold text-[#0B1F4A] min-w-[120px] max-w-[180px] bg-white rounded border border-[#D9E2EC] px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#00A3C4]"
              />
              <div className="flex items-center gap-1">
                <input type="number" min={0} max={200} value={ft.n}
                  onChange={e => updateType(ti, { n: Math.max(0, +e.target.value || 0) })}
                  className="w-14 h-6 text-[10px] text-center rounded border border-[#D9E2EC] bg-white font-semibold text-[#0B1F4A] focus:outline-none focus:ring-1 focus:ring-[#00A3C4]"
                />
                <span className="text-[9px] text-[#8899AA]">voertuigen</span>
              </div>
              <div className="flex items-center gap-1">
                <input type="number" min={0} max={500} step={1} value={Math.round(ft.kwhPerVehicle / 1000)}
                  onChange={e => updateType(ti, { kwhPerVehicle: Math.max(0, (+e.target.value || 0) * 1000) })}
                  className="w-16 h-6 text-[10px] text-center rounded border border-[#D9E2EC] bg-white font-semibold text-[#0B1F4A] focus:outline-none focus:ring-1 focus:ring-[#00A3C4]"
                />
                <span className="text-[9px] text-[#8899AA]">MWh/vt/j</span>
              </div>
              <span className="text-[9px] text-[#8899AA] ml-auto">{(ft.n * ft.kwhPerVehicle / 1e3).toFixed(0)} MWh</span>
              {config.length > 1 && (
                <button onClick={() => removeVehicleType(ti)} className="text-[9px] text-[#EF4444] hover:underline cursor-pointer ml-1" title="Verwijder type">🗑️</button>
              )}
            </div>

            {/* Windows */}
            <div className="space-y-1 ml-3">
              {ft.windows.map((w, wi) => (
                <div key={wi} className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] text-[#8899AA] w-14">Venster {wi + 1}:</span>
                  <select value={w.start} onChange={e => updateWindow(ti, wi, { start: +e.target.value })}
                    className="h-5 text-[9px] rounded border border-[#D9E2EC] bg-white text-[#0B1F4A] px-1 focus:outline-none">
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                  <span className="text-[9px] text-[#8899AA]">—</span>
                  <select value={w.end} onChange={e => updateWindow(ti, wi, { end: +e.target.value })}
                    className="h-5 text-[9px] rounded border border-[#D9E2EC] bg-white text-[#0B1F4A] px-1 focus:outline-none">
                    {HOUR_OPTIONS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                  </select>
                  <input type="number" min={0} max={100} value={w.pct}
                    onChange={e => updateWindow(ti, wi, { pct: Math.max(0, Math.min(100, +e.target.value || 0)) })}
                    className="w-10 h-5 text-[9px] text-center rounded border border-[#D9E2EC] bg-white font-semibold text-[#0B1F4A] focus:outline-none"
                  />
                  <span className="text-[9px] text-[#8899AA]">%</span>
                  {ft.windows.length > 1 && (
                    <button onClick={() => removeWindow(ti, wi)} className="text-[9px] text-[#EF4444] hover:underline cursor-pointer">✕</button>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-2">
                {ft.windows.length < 3 && (
                  <button onClick={() => addWindow(ti)} className="text-[9px] text-[#00A3C4] hover:underline cursor-pointer">+ Venster</button>
                )}
                {!valid && ft.n > 0 && (
                  <span className="text-[9px] text-[#EF4444] font-semibold">⚠ Vensters tellen op tot {pctSum.toFixed(0)}% (moet 100% zijn)</span>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Add vehicle type */}
      {config.length < 10 && (
        <button onClick={addVehicleType}
          className="w-full py-1.5 text-[10px] font-semibold text-[#00A3C4] rounded-lg border border-dashed border-[#D9E2EC] bg-white hover:bg-[#F5F8FB] cursor-pointer transition-colors">
          ➕ Voertuigtype toevoegen
        </button>
      )}

      {/* Totals */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#0B1F4A] text-white">
        <span className="text-[11px] font-semibold">Totaal: {totalVehicles} voertuigen</span>
        <span className="text-[11px] font-semibold">{totalMwh.toLocaleString('nl-NL', {maximumFractionDigits:0})} MWh/jaar</span>
      </div>

      {/* 24-hour load profile bar chart */}
      {totalVehicles > 0 && <LoadProfileChart config={config} />}
    </div>
  );
}
